import {
	createContext,
	memo,
	ReactNode,
	useContext,
	useEffect,
	useLayoutEffect,
	useState,
} from 'react'
import { useRecordingTrainer } from './use-recording-trainer'
import type { RecordingState } from './use-recording-trainer'

export type {
	RecordingState,
	RecordingUiState,
	RecordingUiStatus,
	RecordingUiSeverity,
} from './use-recording-trainer.js'
export {
	getRecordingStatusMessageKey,
	getRecordingStatusSeverity,
} from './use-recording-trainer.js'
export { useRecordingTrainer } from './use-recording-trainer'
export type { RecordingUserState } from './use-recording-user'

const RecordingStateContext = createContext<RecordingState | null>(null)

/**
 * Runs the recording hook and hands each new state up. Memoised, so it
 * re-renders only when its own state changes - handing the state up
 * re-renders the scope, and without `memo` that would re-render this and loop.
 */
const RecordingStateSource = memo(function RecordingStateSource({
	onState,
}: {
	onState: (state: RecordingState | null) => void
}): null {
	const recordingState = useRecordingTrainer()

	// Before paint, so whatever reads the state has it in the first frame.
	useLayoutEffect(() => {
		onState(recordingState)
	})
	useEffect(() => () => onState(null), [onState])

	return null
})

/**
 * Recording state for the subtree while `active`.
 *
 * The subtree keeps its place in the tree either way. Wrapping it in a
 * provider only when active - and in a fragment otherwise - remounted the
 * whole room each time the answer changed: the session mode settles after the
 * first render, and changes again at a blended training's chunk switch. That
 * re-subscribed the event streams and threw away open menus.
 */
export const RecordingStateScope = ({
	active,
	children,
}: {
	active: boolean
	children: ReactNode
}): JSX.Element => {
	const [recordingState, setRecordingState] = useState<RecordingState | null>(
		null
	)

	return (
		<RecordingStateContext.Provider value={active ? recordingState : null}>
			{active ? <RecordingStateSource onState={setRecordingState} /> : null}
			{children}
		</RecordingStateContext.Provider>
	)
}

/** For what only exists while recording applies - see `RecordingStateScope`. */
export const useRecordingState = (): RecordingState => {
	const context = useContext(RecordingStateContext)

	if (!context) {
		throw new Error(
			'useRecordingState must be used where recording applies (RecordingStateScope)'
		)
	}

	return context
}

export const useOptionalRecordingState = (): RecordingState | null => {
	return useContext(RecordingStateContext)
}
